import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { z } from 'zod';

import { IncidentCreate, ServiceIncident } from '@/types/service/serviceIncident';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { SimpleDateTimePicker } from '@/components/shared/SimpleDateTimePicker';

import { useGetSimpleClientEnvironmentsQuery } from '@/store/api/clientEnvironmentApi';

import { zodResolver } from '@hookform/resolvers/zod';

const incidentFormSchema = z.object({
  isGlobal: z.boolean(),
  client_short_name: z.string().optional(),
  environment: z.string().optional(),
  incident_type: z.enum(['event', 'maintenance']),
  title: z.string().min(1, 'Title is required').max(255, 'Title too long'),
  description: z.string().min(1, 'Description is required').max(2000, 'Description too long'),
  status: z.enum(['ongoing', 'resolved']),
  start_time: z.string(),
  end_time: z.string().optional(),
  send_notification: z.boolean(),
});

type FormData = z.infer<typeof incidentFormSchema>;

interface IncidentFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: IncidentCreate) => Promise<void>;
  incident?: ServiceIncident | null;
  isSubmitting?: boolean;
}

export const IncidentFormModal = ({
  open,
  onOpenChange,
  onSubmit,
  incident,
  isSubmitting = false,
}: IncidentFormModalProps) => {
  const isEditMode = !!incident;
  const [isGlobal, setIsGlobal] = useState(false);

  // Fetch client environments
  const { data: clientEnvData } = useGetSimpleClientEnvironmentsQuery();

  // Extract unique clients and create client-to-environments mapping
  const { clients, clientEnvironments } = useMemo(() => {
    if (!clientEnvData?.environments) {
      return { clients: [], clientEnvironments: {} };
    }

    const clientSet = new Set<string>();
    const envMap: Record<string, string[]> = {};

    clientEnvData.environments.forEach((env) => {
      clientSet.add(env.client_short_name);
      if (!envMap[env.client_short_name]) {
        envMap[env.client_short_name] = [];
      }
      if (!envMap[env.client_short_name].includes(env.environment)) {
        envMap[env.client_short_name].push(env.environment);
      }
    });

    return {
      clients: Array.from(clientSet).sort(),
      clientEnvironments: envMap,
    };
  }, [clientEnvData]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(incidentFormSchema),
    defaultValues: {
      isGlobal: false,
      client_short_name: '',
      environment: '',
      incident_type: 'event',
      title: '',
      description: '',
      status: 'ongoing',
      start_time: new Date().toISOString(),
      end_time: '',
      send_notification: false,
    },
  });

  const watchIncidentType = watch('incident_type');
  const watchStatus = watch('status');

  // Populate form when editing or reset when creating new
  useEffect(() => {
    if (open) {
      if (incident) {
        // Check if it's a global incident - handle both null and 'global' string
        const isGlobalIncident =
          !incident.client_short_name ||
          incident.client_short_name === 'global' ||
          !incident.environment ||
          incident.environment === 'global';
        setIsGlobal(isGlobalIncident);

        reset({
          isGlobal: isGlobalIncident,
          client_short_name: isGlobalIncident ? '' : incident.client_short_name || '',
          environment: isGlobalIncident ? '' : incident.environment || '',
          incident_type: incident.incident_type,
          title: incident.title,
          description: incident.description,
          status: incident.status,
          start_time: incident.start_time,
          end_time: incident.end_time || '',
          send_notification: false,
        });
      } else {
        // Reset form when creating new incident
        reset({
          isGlobal: false,
          client_short_name: '',
          environment: '',
          incident_type: 'event',
          title: '',
          description: '',
          status: 'ongoing',
          start_time: new Date().toISOString(),
          end_time: '',
          send_notification: false,
        });
        setIsGlobal(false);
      }
    }
  }, [incident, reset, open]);

  const handleFormSubmit = async (data: FormData) => {
    const submitData: IncidentCreate = {
      client_short_name: data.isGlobal ? null : data.client_short_name || null,
      environment: data.isGlobal ? null : data.environment || null,
      incident_type: data.incident_type,
      title: data.title,
      description: data.description,
      status: data.status,
      start_time: data.start_time,
      end_time: data.end_time || null,
      send_notification: data.send_notification,
    };

    await onSubmit(submitData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Incident' : 'Create New Incident'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the incident details below'
              : 'Create a new service incident or maintenance window'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-4'>
          {/* Global Incident Toggle */}
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='isGlobal'
              checked={isGlobal}
              onCheckedChange={(checked) => {
                setIsGlobal(checked === true);
                setValue('isGlobal', checked === true);
                if (checked) {
                  setValue('client_short_name', '');
                  setValue('environment', '');
                }
              }}
            />
            <Label htmlFor='isGlobal' className='text-sm font-medium cursor-pointer'>
              Global Incident (affects all clients/environments)
            </Label>
          </div>

          {/* Client and Environment */}
          {!isGlobal && (
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='client_short_name'>Client</Label>
                <Select
                  value={watch('client_short_name')}
                  onValueChange={(value) => {
                    setValue('client_short_name', value);
                    // Reset environment when client changes
                    setValue('environment', '');
                  }}
                >
                  <SelectTrigger id='client_short_name'>
                    <SelectValue placeholder='Select client' />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client} value={client}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_short_name && (
                  <p className='text-sm text-destructive'>{errors.client_short_name.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='environment'>Environment</Label>
                <Select
                  value={watch('environment')}
                  onValueChange={(value) => setValue('environment', value)}
                  disabled={!watch('client_short_name')}
                >
                  <SelectTrigger id='environment'>
                    <SelectValue placeholder='Select environment' />
                  </SelectTrigger>
                  <SelectContent>
                    {watch('client_short_name') &&
                      clientEnvironments[watch('client_short_name') || '']?.map((env: string) => (
                        <SelectItem key={env} value={env}>
                          {env}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {errors.environment && (
                  <p className='text-sm text-destructive'>{errors.environment.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Incident Type and Status */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='incident_type'>Incident Type</Label>
              <Select
                value={watchIncidentType}
                onValueChange={(value) =>
                  setValue('incident_type', value as 'event' | 'maintenance')
                }
              >
                <SelectTrigger id='incident_type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='event'>Event</SelectItem>
                  <SelectItem value='maintenance'>Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='status'>Status</Label>
              <Select
                value={watchStatus}
                onValueChange={(value) => setValue('status', value as 'ongoing' | 'resolved')}
              >
                <SelectTrigger id='status'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ongoing'>Ongoing</SelectItem>
                  <SelectItem value='resolved'>Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title */}
          <div className='space-y-2'>
            <Label htmlFor='title'>Title</Label>
            <Input id='title' placeholder='Brief incident title' {...register('title')} />
            {errors.title && <p className='text-sm text-destructive'>{errors.title.message}</p>}
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              placeholder='Detailed description of the incident...'
              rows={4}
              {...register('description')}
            />
            {errors.description && (
              <p className='text-sm text-destructive'>{errors.description.message}</p>
            )}
          </div>

          {/* Start Time and End Time */}
          <div className='space-y-4'>
            <SimpleDateTimePicker
              label='Start Date & Time'
              id='start_time'
              value={watch('start_time') ? new Date(watch('start_time')) : undefined}
              onChange={(date: Date | undefined) => {
                if (date) {
                  setValue('start_time', date.toISOString());
                }
              }}
            />

            <SimpleDateTimePicker
              label='End Date & Time - Optional'
              id='end_time'
              value={
                watch('end_time') && watch('end_time') !== ''
                  ? new Date(watch('end_time')!)
                  : undefined
              }
              onChange={(date: Date | undefined) => {
                if (date) {
                  setValue('end_time', date.toISOString());
                } else {
                  setValue('end_time', '');
                }
              }}
            />
          </div>

          {/* Send Notification Toggle */}
          <div className='flex items-center space-x-2 p-4 bg-muted rounded-md'>
            <Checkbox
              id='send_notification'
              checked={watch('send_notification')}
              onCheckedChange={(checked) => setValue('send_notification', checked === true)}
            />
            <div className='flex-1'>
              <Label htmlFor='send_notification' className='text-sm font-medium cursor-pointer'>
                Send email notification to subscribed users
              </Label>
              <p className='text-xs text-muted-foreground mt-1'>
                Notify all users who subscribed to status updates for{' '}
                {isGlobal ? 'all clients' : 'this client'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Incident' : 'Create Incident'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
